import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { TradingAccount } from "@/lib/api/otc";
import { Customer } from "@/lib/api/reports";
import { ContractType } from "@/lib/api/otc";
import { useBearStore } from "@/section/otc/ContractForm";
import { OtcContractFormV2, contractToForm } from "@/section/otc/contract-form-v2";
import { OtcFormValues } from "@/section/otc/contract-form-v2/types";
import { useOtcContractFormHandlers } from "./useOtcContractFormHandlers";

const NEW_CONTRACT_INITIAL_VALUES: OtcFormValues = {
  date: new Date(),
  crop_year: new Date().getFullYear(),
  contract_type: ContractType.Range,
};

interface OtcContractFormModalProps {
  isPreviewOnly?: boolean;
  isNew: boolean;
  customers: Customer[];
  accounts: TradingAccount[];
  refetchContracts: () => void;
}

export function OtcContractFormModal({
  isPreviewOnly,
  isNew,
  customers,
  accounts,
  refetchContracts,
}: OtcContractFormModalProps) {
  const new_contract = useBearStore((state) => state.new_contract);
  const editing_contract = useBearStore((state) => state.editing_contract);
  const isModalOpen = isNew ? !!new_contract : !!editing_contract?.id;

  const set_new_contract = useBearStore((state) => state.set_new_contract);
  const set_edit_contract = useBearStore((state) => state.set_edit_contract);

  const onUpdateStatus = (status?: boolean) => {
    if (isNew) {
      set_new_contract(
        status === true ? { contract_type: ContractType.Range } : undefined,
      );
    } else {
      set_edit_contract(status === true ? {} : undefined);
    }
  };

  const onCloseModal = () => {
    if (isNew) {
      set_new_contract(undefined);
    } else {
      set_edit_contract(undefined);
    }
  };

  const { onSubmitForm, isSaving } = useOtcContractFormHandlers({
    refetchContracts,
    onCloseModal,
  });

  const isUpdateForm = !isNew;

  // Convert zustand contract data to V2 form initial values
  const initialValues: OtcFormValues = isNew
    ? (new_contract
        ? contractToForm(new_contract as unknown as Record<string, unknown>)
        : NEW_CONTRACT_INITIAL_VALUES)
    : (editing_contract
        ? contractToForm(editing_contract as unknown as Record<string, unknown>)
        : {});

  return (
    <Dialog
      onOpenChange={onUpdateStatus}
      open={isModalOpen}
      defaultOpen={false}
    >
      {isNew && (
        <DialogTrigger asChild>
          <Button
            type="button"
            className="mr-4"
            disabled={isModalOpen}
            onClick={() => onUpdateStatus(true)}
          >
            New Contract
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-screen-2xl md:min-w-sm lg:min-w-[80vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isPreviewOnly
              ? "Preview Contract"
              : isUpdateForm
                ? "Update Contract"
                : "New Contract"}
          </DialogTitle>
        </DialogHeader>
        {isModalOpen && (
          <OtcContractFormV2
            isPreviewOnly={isPreviewOnly}
            accounts={accounts}
            customers={customers}
            isUpdateForm={isUpdateForm}
            initialValues={initialValues}
            onSubmit={(formData) =>
              onSubmitForm(formData, isUpdateForm)
            }
          />
        )}
        <DialogFooter className="sm:justify-start"></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

